import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../styles/StyledButtons';
import type { DocumentPickTarget } from './editorSidebarContext';
import type { Box as CropBox, BoxHandle, SnapGuides } from './cropAdjustBoxes';
import DocumentImportScopeBar, { DocumentImportScopeMode } from './DocumentImportScopeBar';
import { DocumentCropCanvas, DocumentPageThumbStrip } from './DocumentViewerParts';

interface LibraryImportControls {
  scopeMode: DocumentImportScopeMode;
  fromPage: number;
  toPage: number;
  setScopeMode: (mode: DocumentImportScopeMode) => void;
  setFromPage: (page: number) => void;
  setToPage: (page: number) => void;
}

interface DocumentViewerDialogBodyProps {
  documentAsset: DocumentAsset;
  backendService: BackendService;
  currentPage: number;
  pageCount: number;
  pageUrl: string | null;
  mode: 'image' | 'structure';
  confirmError: string | null;
  lastImportedRange?: { fromPage: number; toPage: number } | null;
  wrapRef: React.RefObject<HTMLDivElement>;
  imageDims: { width: number; height: number } | null;
  boxes: CropBox[];
  scale: number;
  guides: SnapGuides;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
  libraryImport?: LibraryImportControls | null;
  onSelectPage: (n: number) => void;
  onModeChange: (m: 'image' | 'structure') => void;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

/** The thumbnail strip's highlighted page range for the current scope, if any. */
function resolveRangeBound(
  libraryImport: LibraryImportControls | null | undefined,
  pageCount: number,
  bound: 'from' | 'to',
): number | undefined {
  if (!libraryImport) return undefined;
  if (libraryImport.scopeMode === 'range') {
    return bound === 'from' ? libraryImport.fromPage : libraryImport.toPage;
  }
  if (libraryImport.scopeMode === 'document') {
    return bound === 'from' ? 1 : pageCount;
  }
  return undefined;
}

/** StyledModal's own `subtitle` prop, rather than a second DialogTitle line. */
export function viewerSubtitle(pickTarget: DocumentPickTarget | null): string | undefined {
  if (pickTarget) return `Use in ${pickTarget.label}`;
  return undefined;
}

function confirmLabel(libraryImport: boolean, confirming: boolean, mode: 'image' | 'structure') {
  if (libraryImport) return confirming ? 'Importing…' : 'Import with AI';
  return mode === 'structure' ? 'Extract content' : 'Insert image';
}

export function ViewerActions({
  confirming,
  hasImported,
  pageReady,
  pickTarget,
  libraryImport,
  mode,
  onClose,
  onConfirm,
}: {
  confirming: boolean;
  hasImported: boolean;
  pageReady: boolean;
  pickTarget: DocumentPickTarget | null;
  libraryImport?: LibraryImportControls | null;
  mode: 'image' | 'structure';
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <>
      <StyledButton
        noTopSpacing
        buttonVariant="inactiveOrCancel"
        sx={SECONDARY_BUTTON_HOVER_SX}
        onClick={onClose}
        disabled={confirming}
      >
        {hasImported ? 'Done' : 'Cancel'}
      </StyledButton>
      <StyledButton
        noTopSpacing
        onClick={onConfirm}
        disabled={confirming || !pageReady || (!pickTarget && !libraryImport)}
        startIcon={
          confirming ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined
        }
      >
        {confirmLabel(!!libraryImport, confirming, mode)}
      </StyledButton>
    </>
  );
}

/** The modal's content: page thumbnails beside the scope picker and page preview. The title,
 * subtitle and action buttons are rendered by StyledModal itself (see DocumentViewerDialog). */
export default function DocumentViewerDialogBody(props: DocumentViewerDialogBodyProps) {
  const {
    documentAsset,
    backendService,
    currentPage,
    pageCount,
    pageUrl,
    mode,
    confirmError,
    lastImportedRange,
    wrapRef,
    imageDims,
    boxes,
    scale,
    guides,
    begin,
    libraryImport,
    onSelectPage,
    onModeChange,
    onImageLoad,
  } = props;
  const pageReady = imageDims !== null;
  return (
    <Box sx={{ display: 'flex', gap: 2, height: '65vh', overflow: 'hidden' }}>
      <DocumentPageThumbStrip
        pageCount={pageCount}
        currentPage={currentPage}
        rangeFrom={resolveRangeBound(libraryImport, pageCount, 'from')}
        rangeTo={resolveRangeBound(libraryImport, pageCount, 'to')}
        documentAsset={documentAsset}
        backendService={backendService}
        onSelectPage={onSelectPage}
      />
      <BoxWithScope
        libraryImport={libraryImport}
        pageCount={pageCount}
        pageReady={pageReady}
        mode={mode}
        onModeChange={onModeChange}
        pageUrl={pageUrl}
        currentPage={currentPage}
        wrapRef={wrapRef}
        imageDims={imageDims}
        boxes={boxes}
        scale={scale}
        guides={guides}
        begin={begin}
        onImageLoad={onImageLoad}
        confirmError={confirmError}
        lastImportedRange={lastImportedRange}
      />
    </Box>
  );
}

function BoxWithScope({
  libraryImport,
  pageCount,
  pageReady,
  mode,
  onModeChange,
  pageUrl,
  currentPage,
  wrapRef,
  imageDims,
  boxes,
  scale,
  guides,
  begin,
  onImageLoad,
  confirmError,
  lastImportedRange,
}: {
  libraryImport?: LibraryImportControls | null;
  pageCount: number;
  pageReady: boolean;
  mode: 'image' | 'structure';
  onModeChange: (m: 'image' | 'structure') => void;
  pageUrl: string | null;
  currentPage: number;
  wrapRef: React.RefObject<HTMLDivElement>;
  imageDims: { width: number; height: number } | null;
  boxes: CropBox[];
  scale: number;
  guides: SnapGuides;
  begin: (index: number, handle: BoxHandle) => (event: React.PointerEvent) => void;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  confirmError: string | null;
  lastImportedRange?: { fromPage: number; toPage: number } | null;
}) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {libraryImport && (
        <DocumentImportScopeBar
          mode={libraryImport.scopeMode}
          fromPage={libraryImport.fromPage}
          toPage={libraryImport.toPage}
          pageCount={pageCount}
          lastImportedRange={lastImportedRange}
          disabled={!pageReady}
          onModeChange={libraryImport.setScopeMode}
          onFromPageChange={libraryImport.setFromPage}
          onToPageChange={libraryImport.setToPage}
        />
      )}
      <DocumentCropCanvas
        mode={mode}
        onModeChange={onModeChange}
        pageUrl={pageUrl}
        currentPage={currentPage}
        wrapRef={wrapRef}
        imageDims={imageDims}
        boxes={boxes}
        scale={scale}
        guides={guides}
        begin={begin}
        onImageLoad={onImageLoad}
        confirmError={confirmError}
        showModeTabs={!libraryImport}
        showCrop={!libraryImport || libraryImport.scopeMode === 'region'}
      />
    </Box>
  );
}
